import { cloneDeep } from "lodash";

import {
  type ChartConfig,
  type ChartState,
  type Compare,
  type MainMetric,
} from "../hooks/use-chart-state";

import { type CompareQuery } from "./compare-query";

export const addNewEmptyChart = (chartState: ChartState) => ({
  ...chartState,
  chartConfigs: [
    ...chartState.chartConfigs,
    {
      chartType: "line",
      chartView: "aggregate",
      alignAxesScale: false,
      mainMetric: {
        metricName: "spend",
        filters: [],
        ignoreTableFilters: false,
      },
      comparisons: [],
      zoomEnabled: false,
    } as ChartConfig,
  ],
});

export const updateChartView =
  (chartIndex: number, chartView: "aggregate" | "split") =>
  (chartState: ChartState) => {
    const chartStateCopy = cloneDeep(chartState);
    chartStateCopy.chartConfigs[chartIndex].chartView = chartView;
    return chartStateCopy;
  };

export const updateChartType =
  (chartIndex: number, chartType: "bar" | "line") =>
  (chartState: ChartState) => {
    const chartStateCopy = cloneDeep(chartState);
    chartStateCopy.chartConfigs[chartIndex].chartType = chartType;
    return chartStateCopy;
  };

export const updateAlignAxes =
  (chartIndex: number, alignAxesScale: boolean) => (chartState: ChartState) => {
    const chartStateCopy = cloneDeep(chartState);
    chartStateCopy.chartConfigs[chartIndex].alignAxesScale = alignAxesScale;
    return chartStateCopy;
  };

export const updateZoom =
  (chartIndex: number, zoomEnabled: boolean) => (chartState: ChartState) => {
    const chartStateCopy = cloneDeep(chartState);
    chartStateCopy.chartConfigs[chartIndex].zoomEnabled = zoomEnabled;
    return chartStateCopy;
  };

export const removeLastChart = (chartState: ChartState) => {
  const chartStateCopy = cloneDeep(chartState);
  chartStateCopy.chartConfigs.pop();
  return chartStateCopy;
};

export const addCompareToChartAt =
  (chartIndex: number, compareQuery: CompareQuery) =>
  (chartState: ChartState) => {
    const chartStateCopy = cloneDeep(chartState);
    const chart = chartStateCopy.chartConfigs[chartIndex];
    chartStateCopy.chartConfigs[chartIndex] = {
      ...chart,
      comparisons: [
        ...chart.comparisons,
        {
          filters: [],
          ignoreTableFilters: false,
          ...compareQuery,
        },
      ],
    };
    return chartStateCopy;
  };

export const deleteCompareFromChartAt =
  ({
    chartIndex,
    compareIndex,
  }: {
    chartIndex: number;
    compareIndex: number;
  }) =>
  (chartState: ChartState) => {
    const chartStateCopy = cloneDeep(chartState);
    const chart = chartStateCopy.chartConfigs[chartIndex];
    chart.comparisons.splice(compareIndex, 1);
    return chartStateCopy;
  };

export const updateMainMetricFromChartAt =
  (chartIndex: number, newMetric: Partial<MainMetric> | Partial<Compare>) =>
  (chartState: ChartState) => {
    const chartStateCopy = cloneDeep(chartState);
    const chart = chartStateCopy.chartConfigs[chartIndex];
    const mainMetric = chart.mainMetric;
    chartStateCopy.chartConfigs[chartIndex].mainMetric = {
      ...mainMetric,
      ...newMetric,
    };
    return chartStateCopy;
  };

export const getCompareAt =
  (chartState: ChartState) =>
  ({
    chartIndex,
    compareIndex,
  }: {
    chartIndex: number;
    compareIndex: number;
  }) =>
    chartState.chartConfigs?.[chartIndex]?.comparisons?.[compareIndex];

export const updateCompareFromChartAt =
  (
    { chartIndex, compareIndex }: { chartIndex: number; compareIndex: number },
    compare: Compare,
  ) =>
  (chartState: ChartState) => {
    const chartStateCopy = cloneDeep(chartState);
    const chart = chartStateCopy.chartConfigs[chartIndex];
    const currentCompare = chart.comparisons[compareIndex];
    chart.comparisons[compareIndex] = {
      ...currentCompare,
      ...compare,
    };
    // Remove the second comparison if it's not comparing metrics
    if (
      chart.comparisons.length === 2 &&
      !chart.comparisons.every((compare) => compare.queryKey === "metricName")
    ) {
      chart.comparisons.splice(1, 1);
    }
    return chartStateCopy;
  };

export const removeAllCompareDateRangeFromCharts =
  () => (chartState: ChartState) => {
    const chartStateCopy = cloneDeep(chartState);
    chartStateCopy.chartConfigs.forEach((chart) => {
      chart.comparisons = chart.comparisons.filter(
        (compare) => compare.queryKey !== "compareDateRange",
      );
    });
    return chartStateCopy;
  };

export const removeAllMetricFiltersFromChartAt =
  ({
    chartIndex,
    compareIndex,
  }: {
    chartIndex: number;
    compareIndex?: number;
  }) =>
  (chartState: ChartState) => {
    const chartStateCopy = cloneDeep(chartState);
    const chart = chartStateCopy.chartConfigs[chartIndex];
    if (compareIndex === undefined) {
      chart.mainMetric.filters = [];
      return chartStateCopy;
    }

    chart.comparisons[compareIndex].filters = [];
    return chartStateCopy;
  };
